import React, { useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';

import { Checkbox } from 'uiToolkitV1/Checkbox';
import { Tooltip } from 'uiToolkitV1/Tooltip';

import { validateAndUpdateAccessControl } from '../../../helpers/validateAccessControl';
import {
  updateAccessControl,
  useAccessControlSelector,
  useTemplateMetaDataSelector,
} from '../../../slice/accessControlSlice';
import { AccessControlTableType } from '../../../types';

interface Props {
  colDef: Record<string, any>;
  data: Record<string, any>;
  tableType: AccessControlTableType;
}

const AccessControlCheckbox = ({ colDef, data, tableType }: Props) => {
  const { field } = colDef ?? {};
  const { permittedValues = [], value } = data[field] ?? {};

  const { value: role } = data ?? {};

  const truthyValue = permittedValues?.filter((item) => item !== 'NONE');
  const falsyValue = permittedValues?.filter((item) => item === 'NONE');

  const isChecked = value === truthyValue?.[0];

  const { isEditMode } = useTemplateMetaDataSelector();

  const { accessControl } = useAccessControlSelector();

  // Create a useRef to track if the effect has already run
  // this makes sure that the effect only runs once after onChange
  // as we can't get the latest value after dispatch in onChange
  // and we need the latest value to validate the access control
  const effectRun = useRef(true);

  const dispatch = useDispatch();

  const onChange = (e) => {
    const { checked } = e?.target ?? {};
    dispatch(
      updateAccessControl({
        tableType,
        role,
        privilegeKey: field,
        isMultiSelect: false,
        value: checked ? truthyValue?.[0] : falsyValue?.[0],
      })
    );
    effectRun.current = false;
  };

  useEffect(() => {
    if (isEditMode && !effectRun.current) {
      validateAndUpdateAccessControl(accessControl, dispatch);
      effectRun.current = true;
    }
  }, [isEditMode, accessControl, dispatch, effectRun]);

  const isGoalAdminRow = role === 'GOAL_ADMIN';

  const isDisabled = !isEditMode || isGoalAdminRow;

  const showTooltip = isGoalAdminRow && isEditMode;

  return (
    <Tooltip label={showTooltip ? 'Admin access cannot be changed' : ''}>
      <Box width="100%" height="100%">
        {/* @ts-ignore */}
        <Checkbox isChecked={isChecked} mt="36px" onChange={onChange} isDisabled={isDisabled} />
      </Box>
    </Tooltip>
  );
};

export { AccessControlCheckbox };
